// NEW_FILE_JUSTIFIED: New messages page component
import { useReducer, useState, useMemo, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useAction, useQuery } from 'convex/react';
import { toast } from 'sonner';
import { api } from '../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: Convex generated API lives outside src/; no @convex/ path alias in tsconfig
import { ConversationList } from '@/components/messages/ConversationList';
import { ChatPanel } from '@/components/messages/ChatPanel';
import { ContactPanel } from '@/components/messages/ContactPanel';
import { IconSidebar } from '@/components/messages/IconSidebar';
import {
  ConversationListSkeleton,
  ChatPanelSkeleton,
  ContactPanelSkeleton,
} from '@/components/messages/MessageSkeletons';
import { useTelegramMessages } from '@/hooks/useTelegramMessages';
import { MOCK_TAGS } from '@/constants/messagesMockData';
import { CreateLeadDialog } from '@/components/leads/CreateLeadDialog';
import type { IContactDetail, IReplyContext, IMessage } from '@/@types/messages';
import type {
  IMessagesPageState,
  TMessagesPageAction,
  TConversationFilter,
  TRightPanelView,
  TMessageChannel,
} from '@/@types/messages';
import type { IAILeadExtras, IMatchedService } from '@/@types/leads';

// ============================================================================
// Reducer
// ============================================================================

const initialState: IMessagesPageState = {
  selectedConversationId: '',
  activeRightPanel: 'contact',
  filter: 'all',
  searchQuery: '',
  activeChannel: 'telegram',
};

function messagesPageReducer(
  state: IMessagesPageState,
  action: TMessagesPageAction
): IMessagesPageState {
  switch (action.type) {
    case 'SET_CONVERSATION':
      return { ...state, selectedConversationId: action.payload };
    case 'SET_RIGHT_PANEL':
      return { ...state, activeRightPanel: action.payload };
    case 'SET_FILTER':
      return { ...state, filter: action.payload };
    case 'SET_SEARCH':
      return { ...state, searchQuery: action.payload };
    case 'SET_CHANNEL':
      return { ...state, activeChannel: action.payload };
    default: {
      const _exhaustive: never = action;
      return _exhaustive;
    }
  }
}

// ============================================================================
// Component
// ============================================================================

export function MessagesPage() {
  const [state, dispatch] = useReducer(messagesPageReducer, initialState);

  const [searchParams] = useSearchParams();
  const contactFromUrl = searchParams.get('contact');
  const nameFromUrl = searchParams.get('name');
  const phoneFromUrl = searchParams.get('phone');

  const {
    conversations,
    messages,
    isLoading,
    markRead,
    sendMessage,
    sendMediaMessage,
    sendReaction,
    deleteMessage,
    contactDetails,
    fetchBio,
  } = useTelegramMessages(
    state.selectedConversationId !== '' ? state.selectedConversationId : null
  );

  const generateUploadUrl = useMutation(api.storage.createUploadUrl);
  const currentOrg = useQuery(api.organisations.queries.getCurrentOrg);
  const orgServiceTypes = useQuery(
    api.serviceTypes.listByOrg,
    currentOrg ? { orgId: currentOrg._id } : 'skip'
  );
  const [isSendingFile, setIsSendingFile] = useState(false);
  const [replyContext, setReplyContext] = useState<IReplyContext | null>(null);
  const [createLeadOpen, setCreateLeadOpen] = useState(false);
  const [createLeadPrefill, setCreateLeadPrefill] = useState({ name: '', email: '', phone: '' });
  const [aiExtras, setAiExtras] = useState<IAILeadExtras | undefined>(undefined);
  const [matchedServices, setMatchedServices] = useState<IMatchedService[] | undefined>(undefined);
  const [unmatchedServices, setUnmatchedServices] = useState<string[] | undefined>(undefined);
  const [isExtracting, setIsExtracting] = useState(false);
  const extractLeadData = useAction(api.leads.aiLeadExtraction.extractLeadData);

  // Build real contact detail from the selected conversation
  const selectedContact = useMemo((): IContactDetail | null => {
    const conv = conversations.find(c => c.id === state.selectedConversationId);
    if (conv !== undefined) {
      return {
        id: conv.id,
        name: conv.contactName,
        initials: conv.contactInitials,
        avatarColor: conv.avatarColor,
        contactType: conv.chatType === 'group' ? 'Group' : 'Telegram',
        contactTypeColor: conv.chatType === 'group' ? '#6366F1' : '#0D9488',
        username: conv.contactUsername,
        phone: contactDetails?.phone,
        bio: contactDetails?.bio,
        chatType: conv.chatType,
        assignedTo: '',
        profilePicUrl: contactDetails?.profilePicUrl ?? conv.profilePicUrl,
      };
    }
    // Virtual contact from URL params (no existing conversation)
    if (contactFromUrl) {
      const name = nameFromUrl ?? phoneFromUrl ?? contactFromUrl;
      return {
        id: contactFromUrl,
        name,
        initials: name.slice(0, 2).toUpperCase(),
        avatarColor: '#229ED9',
        contactType: 'Telegram',
        contactTypeColor: '#0D9488',
        phone: phoneFromUrl ?? undefined,
        chatType: 'personal',
        assignedTo: '',
      };
    }
    return null;
  }, [
    conversations,
    state.selectedConversationId,
    contactDetails,
    contactFromUrl,
    nameFromUrl,
    phoneFromUrl,
  ]);

  const filteredConversations = useMemo(() => {
    let result = conversations;

    if (state.filter === 'personal') {
      result = result.filter(c => c.chatType !== 'group');
    } else if (state.filter === 'groups') {
      result = result.filter(c => c.chatType === 'group');
    } else if (state.filter === 'unread') {
      result = result.filter(c => c.unreadCount > 0);
    }

    if (state.searchQuery.trim()) {
      const query = state.searchQuery.toLowerCase();
      result = result.filter(
        c =>
          c.contactName.toLowerCase().includes(query) || c.lastMessage.toLowerCase().includes(query)
      );
    }

    return result;
  }, [conversations, state.filter, state.searchQuery]);

  const handleSelectConversation = useCallback(
    (id: string) => {
      dispatch({ type: 'SET_CONVERSATION', payload: id });
      markRead(id).catch(() => {
        // Mark read is best-effort — ignore failures
      });
    },
    [markRead]
  );

  const handleFilterChange = useCallback((filter: TConversationFilter) => {
    dispatch({ type: 'SET_FILTER', payload: filter });
  }, []);

  const handleSearchChange = useCallback((query: string) => {
    dispatch({ type: 'SET_SEARCH', payload: query });
  }, []);

  const handleRightPanelChange = useCallback((view: TRightPanelView) => {
    dispatch({ type: 'SET_RIGHT_PANEL', payload: view });
  }, []);

  const handleChannelChange = useCallback((channel: TMessageChannel) => {
    dispatch({ type: 'SET_CHANNEL', payload: channel });
  }, []);

  const handleCreateLead = useCallback(async () => {
    if (!selectedContact) return;

    setIsExtracting(true);
    setAiExtras(undefined);
    setMatchedServices(undefined);
    setUnmatchedServices(undefined);

    try {
      const chatMessages = messages.map(m => ({
        sender: m.isOutgoing ? 'Agent' : selectedContact.name || 'Lead',
        text: m.text,
        timestamp: Date.now(),
        mediaStorageId: m.mediaStorageId,
        mediaType: m.mediaType,
        mediaMimeType: m.mediaMimeType,
        mediaFilename: m.mediaFilename,
      }));

      const serviceTypesForAI = orgServiceTypes?.map(st => ({
        name: st.name,
        requiredFields: st.requiredFields.map(f => ({
          label: f.label,
          fieldType: f.fieldType,
          description: f.description,
          options: f.options,
        })),
      }));

      const result = await extractLeadData({
        type: 'chat',
        platform: 'telegram',
        messages: chatMessages,
        contactName: selectedContact.name,
        contactPhone: selectedContact.phone,
        serviceTypes: serviceTypesForAI,
      });

      setCreateLeadPrefill({
        name: result.name ?? selectedContact.name,
        email: result.email ?? '',
        phone: result.phoneNumber ?? selectedContact.phone ?? '',
      });
      setAiExtras({
        leadType: result.leadType,
        temperature: result.temperature,
        serviceInterest: result.serviceInterest,
        estimatedValue: result.estimatedValue,
        notes: result.notes,
        source: result.source ?? 'telegram',
      });
      setMatchedServices(result.matchedServices);
      setUnmatchedServices(result.unmatchedServices);
    } catch (err) {
      console.error('[MessagesPage] AI lead extraction failed:', err);
      setCreateLeadPrefill({
        name: selectedContact.name,
        email: '',
        phone: selectedContact.phone ?? '',
      });
      setAiExtras({ source: 'telegram' });
    } finally {
      setIsExtracting(false);
      setCreateLeadOpen(true);
    }
  }, [selectedContact, messages, extractLeadData, orgServiceTypes]);

  const handleSendMessage = useCallback(
    async (text: string, replyToMessageId?: string | number) => {
      if (state.selectedConversationId === '') return;

      const selectedConv = conversations.find(c => c.id === state.selectedConversationId);
      const contactName =
        selectedConv?.contactName ?? nameFromUrl ?? phoneFromUrl ?? state.selectedConversationId;
      const chatType = selectedConv?.chatType ?? 'personal';

      try {
        await sendMessage({
          telegramContactId: state.selectedConversationId,
          contactName,
          text,
          chatType,
          // AS_JUSTIFIED: platformMessageId is always number for Telegram (telegramMessageId)
          replyToMessageId: replyToMessageId as number | undefined,
          replyToText: replyContext?.text,
          replyToSenderName: replyContext?.senderName,
        });
        setReplyContext(null); // Clear reply after sending
      } catch (err) {
        console.error('[MessagesPage] sendMessage failed', err);
        toast.error('Failed to send message. Please try again.');
      }
    },
    [
      state.selectedConversationId,
      conversations,
      sendMessage,
      replyContext,
      nameFromUrl,
      phoneFromUrl,
    ]
  );

  const handleReaction = useCallback(
    async (platformMessageId: string | number, emoji: string) => {
      // AS_JUSTIFIED: platformMessageId is always number for Telegram (telegramMessageId from schema)
      await sendReaction(platformMessageId as number, emoji);
    },
    [sendReaction]
  );

  const handleDeleteMessage = useCallback(
    async (platformMessageId: string | number, forEveryone: boolean) => {
      // AS_JUSTIFIED: platformMessageId is always number for Telegram (telegramMessageId from schema)
      await deleteMessage(platformMessageId as number, forEveryone);
    },
    [deleteMessage]
  );

  const handleReplyMessage = useCallback((message: IMessage) => {
    setReplyContext({
      messageId: message.platformMessageId ?? message.id,
      text:
        message.text ??
        message.mediaFilename ??
        (message.mediaType ? `[${message.mediaType}]` : undefined),
      senderName: message.senderName,
    });
  }, []);

  const handleCancelReply = useCallback(() => {
    setReplyContext(null);
  }, []);

  const handleSendFile = useCallback(
    async (file: File, caption: string) => {
      if (state.selectedConversationId === '') return;

      const selectedConv = conversations.find(c => c.id === state.selectedConversationId);
      const contactName =
        selectedConv?.contactName ?? nameFromUrl ?? phoneFromUrl ?? state.selectedConversationId;
      const chatType = selectedConv?.chatType ?? 'personal';

      setIsSendingFile(true);
      try {
        const uploadUrl = await generateUploadUrl();
        const uploadResult = await fetch(uploadUrl, {
          method: 'POST',
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
          body: file,
        });

        if (!uploadResult.ok) {
          throw new Error('Upload failed');
        }

        const { storageId } = (await uploadResult.json()) as { storageId: string }; // AS_JUSTIFIED: Convex upload API always returns { storageId: string }

        const mediaType = file.type.startsWith('image/')
          ? ('image' as const)
          : ('document' as const);

        await sendMediaMessage({
          contactId: state.selectedConversationId,
          contactName,
          storageId,
          mediaType,
          filename: file.name,
          mimeType: file.type,
          size: file.size,
          text: caption || undefined,
          chatType,
        });
      } catch {
        toast.error('Failed to send file. Please try again.');
      } finally {
        setIsSendingFile(false);
      }
    },
    [
      state.selectedConversationId,
      conversations,
      sendMediaMessage,
      generateUploadUrl,
      nameFromUrl,
      phoneFromUrl,
    ]
  );

  // Auto-fetch bio when opening a conversation that hasn't been fetched yet
  // Skip bio fetch for group conversations (groups don't have individual bios)
  useEffect(() => {
    const selectedConv = conversations.find(c => c.id === state.selectedConversationId);
    if (
      state.selectedConversationId !== '' &&
      selectedConv?.chatType !== 'group' &&
      contactDetails !== null &&
      contactDetails.bioFetchedAt === undefined
    ) {
      fetchBio(state.selectedConversationId).catch(() => {
        // Bio fetch is best-effort — ignore failures
      });
    }
  }, [state.selectedConversationId, contactDetails, fetchBio, conversations]);

  // Auto-select conversation from URL param
  useEffect(() => {
    if (contactFromUrl !== null && contactFromUrl !== '' && state.selectedConversationId === '') {
      dispatch({ type: 'SET_CONVERSATION', payload: contactFromUrl });
    }
  }, [contactFromUrl, state.selectedConversationId]);

  return (
    <div className="flex flex-col h-full">
      {/* Page Header */}
      <div className="flex items-center justify-between px-8 py-4 border-b border-border shrink-0">
        <h1 className="text-2xl font-bold text-foreground">Telegram</h1>
      </div>

      {/* Content Area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Loading state announcement for screen readers */}
        <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
          {isLoading ? 'Loading conversations…' : 'Conversations loaded.'}
        </div>
        {isLoading ? (
          <>
            <ConversationListSkeleton />
            <ChatPanelSkeleton />
            <ContactPanelSkeleton />
          </>
        ) : (
          <>
            <ConversationList
              conversations={filteredConversations}
              selectedId={state.selectedConversationId}
              onSelect={handleSelectConversation}
              filter={state.filter}
              onFilterChange={handleFilterChange}
              searchQuery={state.searchQuery}
              onSearchChange={handleSearchChange}
            />

            {state.selectedConversationId !== '' && selectedContact !== null ? (
              <ChatPanel
                contact={selectedContact}
                messages={messages}
                onSendMessage={handleSendMessage}
                onSendFile={handleSendFile}
                isSendingFile={isSendingFile}
                activeChannel={state.activeChannel}
                onChannelChange={handleChannelChange}
                platform="telegram"
                onReaction={handleReaction}
                onDeleteMessage={handleDeleteMessage}
                onReplyMessage={handleReplyMessage}
                replyContext={replyContext}
                onCancelReply={handleCancelReply}
                onCreateLead={handleCreateLead}
              />
            ) : (
              <div
                role="region"
                aria-label="Chat area"
                className="flex flex-1 flex-col items-center justify-center text-muted-foreground"
              >
                <p>Select a conversation to start messaging</p>
              </div>
            )}

            {state.activeRightPanel === 'contact' && selectedContact !== null && (
              <ContactPanel
                contact={selectedContact}
                tags={MOCK_TAGS}
                contactIdentifier={state.selectedConversationId}
                platform="telegram"
              />
            )}
          </>
        )}

        <IconSidebar activeView={state.activeRightPanel} onViewChange={handleRightPanelChange} />
      </div>

      <CreateLeadDialog
        open={createLeadOpen}
        onOpenChange={open => {
          if (!open) {
            setCreateLeadOpen(false);
            setAiExtras(undefined);
            setMatchedServices(undefined);
            setUnmatchedServices(undefined);
          }
        }}
        prefillName={createLeadPrefill.name}
        prefillEmail={createLeadPrefill.email}
        prefillPhone={createLeadPrefill.phone}
        prefillSource={aiExtras?.source}
        aiExtras={aiExtras}
        matchedServices={matchedServices}
        unmatchedServices={unmatchedServices}
        orgServiceTypes={orgServiceTypes?.map(st => ({
          _id: st._id,
          orgId: st.orgId,
          name: st.name,
          requiredFieldCount: st.requiredFields.length,
        }))}
        onCreated={() => {
          setMatchedServices(undefined);
          setUnmatchedServices(undefined);
        }}
      />

      {isExtracting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/50">
          <div className="flex items-center gap-3 rounded-xl bg-card border border-border px-6 py-4 shadow-lg">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <span className="text-sm font-medium text-foreground">Analyzing conversation...</span>
          </div>
        </div>
      )}
    </div>
  );
}
